import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  AlertColor,
  Box,
  Theme,
} from '@mui/material';
import { FC, ReactNode } from 'react';
import Icon from '../icon';
import { createUseStyles } from 'react-jss';
import classNames from 'classnames';
import { getOpaqueSurfaceColors } from '@/lib/theme/opaque-surface-colors';

interface Props {
  title: ReactNode;
  children: ReactNode;
  defaultOpen?: boolean;
  className?: string;
  color?: AlertColor | 'default';
}

const useStyles = createUseStyles((theme: Theme) => {
  const surfaceColors = getOpaqueSurfaceColors(theme);
  return {
    accordionButton: {
      '& .MuiAccordion-root': {
        border: `1px solid ${theme.palette.divider}`,
      },
      '& .MuiAccordionSummary-root': {
        padding: '0px 4px',
        minHeight: 'inherit',
      },
      '& .MuiAccordionSummary-content': {
        margin: 0,
        padding: 8,
      },
      '& .MuiAccordionSummary-expandIconWrapper': {
        marginRight: 2,
      },
      '& .MuiAccordionDetails-root': {
        padding: '8px 8px 8px 12px',
      },
      '&.info': {
        '& .MuiAccordion-root': {
          backgroundColor: surfaceColors.info,
          border: 'none',
        },
        '& .MuiAccordionSummary-content': {
          color: theme.palette.chip.info,
        },
      },
      '&.error': {
        '& .MuiAccordion-root': {
          backgroundColor: surfaceColors.error,
          border: 'none',
        },
        '& .MuiAccordionSummary-content': {
          color: theme.palette.chip.error,
        },
      },
      '&.warning': {
        '& .MuiAccordion-root': {
          backgroundColor: surfaceColors.warning,
          border: 'none',
        },
        '& .MuiAccordionSummary-content': {
          color: theme.palette.chip.warning,
        },
      },
      '&.success': {
        '& .MuiAccordion-root': {
          backgroundColor: surfaceColors.success,
          border: 'none',
        },
        '& .MuiAccordionSummary-content': {
          color: theme.palette.chip.success,
        },
      },
    },
  };
});

const AccordionButton: FC<Props> = ({
  title,
  children,
  defaultOpen = false,
  className,
  color = 'default',
}) => {
  const styles = useStyles();
  const compClass = classNames(styles.accordionButton, className, {
    [color]: color !== 'default',
  });
  return (
    <Box className={compClass}>
      <Accordion elevation={0} defaultExpanded={defaultOpen}>
        <AccordionSummary expandIcon={<Icon name="expand" />}>{title}</AccordionSummary>
        <AccordionDetails>{children}</AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default AccordionButton;
